import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ListTodo, X, Zap } from "lucide-react";
import { Chip } from "@/components/grooming/grooming-chips";
import { Button } from "@/components/ui/button";
import { useProjectSession } from "@/hooks/queries/use-grooming";
import { useGroomingQueue } from "@/hooks/use-grooming-queue";
import {
  nextInLineChip,
  nextInLineHeadline,
  nextInLineSentence,
  nextInLineSummary,
  queueHighlightPath,
  type NextInLineSummary,
} from "@/lib/grooming/next-in-line";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * "AB#30412 is next in line" (epic PI-SPEC-SW-E290, feature
 * PI-SPEC-SW-1482, UX-PM-04, frame B4): after Get a brief on One item, or
 * Groom this item in the reading pane, while the entry waits in Up next. It
 * says where the item is, what it waits for, a rough wait and that a
 * notification follows, with See it in the Queue (the row highlighted) and
 * Cancel (removes the entry).
 *
 * The app decides the place; the card only reads the queue
 * (`useGroomingQueue`) and the project's typical run time and session date
 * (`useProjectSession`). It renders nothing once the entry is not in Up next
 * any more (it started running, finished or was removed), so the page falls
 * back to its own running or result display.
 *
 * `size="compact"` (the reading pane) leaves out the title and the meter.
 */

/** How often the rough wait is worked out again while the card shows. */
const TICK_MS = 30_000;

/** The meter's fill when there is no estimate (frame B4's resting value). */
const DEFAULT_FILL = 0.18;

export interface NextInLineCardProps {
  /** The queue entry to follow. */
  entryId: string | null;
  /** The item's ADO project, for the typical run time and the session date. */
  project: string;
  /** The item's title, when the queue entry has none yet. */
  title?: string | null | undefined;
  size?: "full" | "compact";
  className?: string | undefined;
}

/** What the meter line says while the entry waits. */
function waitingWords(summary: NextInLineSummary): string {
  if (summary.paused) return "Waiting for the queue to carry on";
  if (summary.running === 0) return "Starting";
  return summary.nextInLine && summary.position === 1 ? "Waiting for a free run" : "Waiting for its turn";
}

/** The card while `entryId` waits in Up next; null otherwise. */
export function NextInLineCard({ entryId, project, title, size = "full", className }: NextInLineCardProps) {
  const { state, actions } = useGroomingQueue();
  const [now, setNow] = useState(Date.now);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const waiting = nextInLineSummary(state, entryId) !== null;
  const session = useProjectSession(waiting ? project : undefined);

  useEffect(() => {
    if (!waiting) return;
    const timer = setInterval(() => {
      setNow(Date.now());
    }, TICK_MS);
    return () => {
      clearInterval(timer);
    };
  }, [waiting]);

  const summary = nextInLineSummary(state, entryId, { now, typicalItemSeconds: session.data?.typicalItemSeconds ?? null });
  if (!summary) return null;

  const compact = size === "compact";
  const itemTitle = summary.entry.title ?? title ?? null;
  const sentence = nextInLineSentence(summary, { sessionDate: session.data?.sessionDate ?? null });
  const fill = summary.waitProgress ?? DEFAULT_FILL;

  const cancel = () => {
    setCancelling(true);
    setError(null);
    void actions
      .remove(summary.entry.id)
      .then((result) => {
        if (!result.ok) setError(result.message);
      })
      .finally(() => {
        setCancelling(false);
      });
  };

  return (
    <div
      className={cn(
        "flex flex-col rounded-[10px] border border-[#1E3A5F] bg-[#0D1520]",
        compact ? "gap-2 px-3.5 py-3" : "gap-3 px-[18px] py-4",
        className
      )}
      role="status"
      data-testid="next-in-line-card"
      data-entry-id={summary.entry.id}
      data-size={size}
    >
      <div className="flex items-center gap-2.5">
        <Zap className="h-4 w-4 shrink-0 text-[#6CC4FF]" aria-hidden="true" />
        <p className={cn("min-w-0 flex-1 font-semibold", compact ? "text-[13px]" : "text-sm")} data-testid="next-in-line-headline">
          {nextInLineHeadline(summary)}
        </p>
        <Chip tone="cur" testId="next-in-line-chip">
          {nextInLineChip(summary)}
        </Chip>
      </div>
      {!compact && itemTitle && (
        <p className={cn("truncate", TOOLBOX_TEXT.content)} title={itemTitle}>
          {itemTitle}
        </p>
      )}
      <p
        className={cn("leading-[1.55] text-muted-foreground", compact ? TOOLBOX_TEXT.meta : TOOLBOX_TEXT.content)}
        data-testid="next-in-line-sentence"
      >
        {sentence}
      </p>
      {!compact && (
        <div className="flex items-center gap-3">
          <div
            className="h-1.5 flex-1 overflow-hidden rounded-[3px] bg-[#27272A]"
            role="progressbar"
            aria-label="Wait for a free run"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(fill * 100)}
          >
            <div className="h-full rounded-[3px] bg-[#23A4FF]" style={{ width: `${String(Math.round(fill * 100))}%` }} />
          </div>
          <span className={cn("flex-none", TOOLBOX_TEXT.meta, TOOLBOX_TEXT.faint)}>{waitingWords(summary)}</span>
        </div>
      )}
      {error && (
        <p className={cn("text-[#F87171]", TOOLBOX_TEXT.content)} role="alert">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button asChild variant="outline" size="sm" className="gap-1.5">
          <Link to={queueHighlightPath(summary.entry.id)} data-testid="next-in-line-see-queue">
            <ListTodo className="h-4 w-4" aria-hidden="true" />
            See it in the Queue
          </Link>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="gap-1.5"
          disabled={cancelling}
          onClick={cancel}
          data-testid="next-in-line-cancel"
        >
          <X className="h-4 w-4" aria-hidden="true" />
          Cancel
        </Button>
      </div>
    </div>
  );
}
